import { css } from 'aphrodite';
import * as React from 'react';
import { IPhoneNumber } from '../../../extViewmodels';
import { PhoneIcon } from '../../../web/components/svgs/icons/PhoneIcon';
import { useTelephony } from '../../contexts/telephony';
import { useTysonCoaching } from '../../voice-agent/contexts/TysonCoachingContext';
import { startCoachingBeforeCall } from '../../voice-agent/utils/startCoachingBeforeCall';
import { styleSheet } from './styles';

interface IProps {
	companyId?: string;
	contactId?: string;
	isDisabled?: boolean;
	phoneNumber: IPhoneNumber;
	renderFallback?: (href: string) => JSX.Element;
	renderWithProvider?: (onClick: () => void) => JSX.Element;
	isDirectLine?: boolean;
}

const callHref = (phoneNumber: string) => {
	return `tel:${phoneNumber}`;
};

const CallButtonWrapper: React.FC<IProps> = ({
	companyId,
	contactId,
	isDisabled,
	phoneNumber,
	renderFallback,
	renderWithProvider,
}) => {
	const { initiateCall, phoneCall, telephonyConfiguration } = useTelephony();
	const coaching = useTysonCoaching();
	const [isStartingCoaching, setIsStartingCoaching] = React.useState(false);

	const hasProvider = telephonyConfiguration?.provider;
	const phoneNumberE164 = phoneNumber?.metadata?.e164;

	const call = async () => {
		// Start coaching before initiating the call (staging and dev only, not production)
		if (import.meta.env.MODE !== 'production' && companyId && contactId) {
			// Only initialize coaching if not already active and ready
			const alreadyReady = coaching.isActive && coaching.conversationId && !coaching.isCreatingConversation;

			if (!alreadyReady) {
				setIsStartingCoaching(true);
				try {
					await startCoachingBeforeCall({
						companyId,
						contactId,
						startCoaching: coaching.startCoaching,
						getConversationId: () => coaching.conversationId,
						getIsAIResponding: () => coaching.isAIResponding,
						getIsCreatingConversation: () => coaching.isCreatingConversation,
					});
				} catch (error) {
					console.error('Failed to start pre-call coaching:', error);
					// Continue with call even if coaching fails
				} finally {
					setIsStartingCoaching(false);
				}
			}
		}

		initiateCall(phoneNumber, companyId, contactId);
	};

	const onClick = !isDisabled && !phoneCall && !isStartingCoaching ? call : null;

	if (hasProvider) {
		return renderWithProvider(onClick);
	}

	return renderFallback(callHref(phoneNumberE164));
};

export const CallButton: React.FC<IProps> = props => {
	const renderFallback = (href: string) => (
		<a className={css(styleSheet.callCta)} href={href}>
			<span>
				<PhoneIcon fill='#fff' />
				<span>Call</span>
			</span>
		</a>
	);

	const renderWithProvider = (onClick: () => void) => (
		<button className={css(styleSheet.callCta)} onClick={onClick}>
			<span>
				<PhoneIcon fill='#fff' />
				<span>Call</span>
			</span>
		</button>
	);

	return <CallButtonWrapper {...props} renderFallback={renderFallback} renderWithProvider={renderWithProvider} />;
};

export const DirectLineCallButton: React.FC<IProps> = props => {
	const renderFallback = (href: string) => (
		<a className={css(styleSheet.directLineCta)} href={href}>
			<span>
				<PhoneIcon fill='#fff' />
				<span>Direct</span>
			</span>
		</a>
	);

	const renderWithProvider = (onClick: () => void) => (
		<button className={css(styleSheet.directLineCta)} onClick={onClick}>
			<span>
				<PhoneIcon fill='#fff' />
				<span>Direct</span>
			</span>
		</button>
	);

	return <CallButtonWrapper {...props} renderFallback={renderFallback} renderWithProvider={renderWithProvider} />;
};

export const SmallCallButton: React.FC<IProps> = props => {
	const renderFallback = (href: string) => (
		<a className={css(styleSheet.smallCallButton)} href={href}>
			<span>Call</span>
		</a>
	);

	const renderWithProvider = (onClick: () => void) => (
		<button className={css(styleSheet.smallCallButton)} onClick={onClick}>
			<span>Call</span>
		</button>
	);

	return <CallButtonWrapper {...props} renderFallback={renderFallback} renderWithProvider={renderWithProvider} />;
};

export const SmallDirectLineButton: React.FC<IProps> = props => {
	const renderFallback = (href: string) => (
		<a className={css(styleSheet.smallDirectLineButton)} href={href}>
			<span>Direct</span>
		</a>
	);

	const renderWithProvider = (onClick: () => void) => (
		<button className={css(styleSheet.smallDirectLineButton)} onClick={onClick}>
			<span>Direct</span>
		</button>
	);

	return <CallButtonWrapper {...props} renderFallback={renderFallback} renderWithProvider={renderWithProvider} />;
};
